import { mstCommon } from '@libs/domain';
import { characterCode, importType } from '@libs/enums';
import type { ItemName, PageProps } from '@ps-smart-cp/ppfw-client';
import {
  FileField,
  RadioField,
  SelectBoxField,
  Template,
  TextStringField,
  getNameWithDefault,
  useGuideTab,
  useLocalizedContents,
  useTry,
  useValidator,
} from '@ps-smart-cp/ppfw-client';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MstImportFooterButtons } from '../components';
import { mstImportFieldConfig } from '../item-config';
import { MstImport } from '../models';
import type { ImportFunction } from '../repositories';

interface MstImportTmplProps<T extends MstImport> {
  /**
   * マスタの機能名
   */
  mstName: ItemName;
  importFunction: ImportFunction<T>;
  initMstImport?: () => T;
}

function MstImportTmpl<T extends MstImport>({
  mstName,
  importFunction,
  initMstImport,
  rerender,
  rerenderContentsOnly,
}: MstImportTmplProps<T> & PageProps) {
  const navigate = useNavigate();

  const { locale } = useLocalizedContents();
  const { submitNewJob } = useGuideTab();
  const { errorFields, validateThenTryRequest } = useTry();
  const validator = useValidator();

  const [mstImport, setMstImport] = useState(initMstImport ?? MstImport.baseInit);

  const omImport = (fn: ImportFunction<T>) => {
    return validateThenTryRequest(
      () => validator.with(mstImport, mstImportFieldConfig).execute(),
      async () => {
        const result = await fn(mstImport.file, mstImport as T);
        submitNewJob(result.jobNo);
        rerenderContentsOnly();
      },
    );
  };

  return (
    <Template
      title={
        getNameWithDefault(mstName.displayName, locale) +
        getNameWithDefault(mstCommon.titleNames.import.displayName, locale)
      }
      footerButtonGroup={
        <MstImportFooterButtons
          onImportClick={omImport(importFunction)}
          onResetClick={rerender}
          onToMaintenanceClick={() => navigate('../')}
        />
      }
    >
      <FileField
        items={mstImportFieldConfig}
        obj={mstImport}
        setObj={setMstImport}
        prop="file"
        errorFields={errorFields}
      />
      <RadioField
        items={mstImportFieldConfig}
        obj={mstImport}
        setObj={setMstImport}
        prop="importClass"
        enumNameConfig={importType.nameConfig}
        direction="vertical"
        enumType={importType.enum}
        errorFields={errorFields}
      />
      <TextStringField
        items={mstImportFieldConfig}
        obj={mstImport}
        setObj={setMstImport}
        prop="jobComment"
        errorFields={errorFields}
      />
      <SelectBoxField
        items={mstImportFieldConfig}
        obj={mstImport}
        setObj={setMstImport}
        prop="characterCode"
        enumNameConfig={characterCode.nameConfig}
        enumType={characterCode.enum}
        needEmptyOption
        errorFields={errorFields}
      />
    </Template>
  );
}

export { MstImportTmpl };
